import { formatDistanceToNow } from 'date-fns';
import { ExternalLink, Loader2, RefreshCcw } from 'lucide-react';
import React from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { VOICE_QA_VERDICT_BADGES } from '@apps/web/components/voice_calls/voice_qa_badges';
import { cn } from '@apps/web/lib/utils';
import { formatBucketSlug } from '@apps/web/routes/_app/admin/voice-qa/bucket_formatting';
import { useVoiceQACall } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type ApiResponseType } from '@libraries/typescript/api_client';

type VoiceQACall = ApiResponseType<'/admin/voice-qa/{call_id}', 'get'>;
type AgentRating = NonNullable<VoiceQACall['ratings']['agent']>;
type AuditRun = VoiceQACall['audit_runs'][number];

const ACTIVE_CALL_STATUSES = new Set([
    'initiated',
    'in_progress',
    'ongoing',
    'queued',
    'ringing',
]);

function getPendingCopy(
    qaCall: VoiceQACall | undefined,
    callStatus: string | null | undefined,
) {
    if (!qaCall) {
        return 'Automated QA status is unavailable right now.';
    }

    const hasDeveloperTimeline = (qaCall.developer_transcript?.length ?? 0) > 0;
    if (!hasDeveloperTimeline) {
        return 'This call does not have a developer timeline yet, so automated QA is not available.';
    }

    const normalizedStatus = (qaCall.call_status ?? callStatus ?? '')
        .trim()
        .toLowerCase()
        .replace(/-/g, '_');
    if (ACTIVE_CALL_STATUSES.has(normalizedStatus)) {
        return 'The call is still in progress. Automated QA will appear after it ends.';
    }

    return 'No automated QA verdict has been persisted yet.';
}

function RatingBody({ rating }: { readonly rating: AgentRating }) {
    const issueCount = rating.issues?.length ?? 0;
    const verdictBadge = VOICE_QA_VERDICT_BADGES[rating.label];

    return (
        <div className="bg-muted/20 min-w-0 space-y-3 rounded-lg border p-3">
            <div className="flex flex-wrap items-center gap-2">
                <Badge
                    variant="secondary"
                    className={cn('text-xs', verdictBadge.className)}
                >
                    {verdictBadge.label}
                </Badge>
                {issueCount > 0 ? (
                    <Badge variant="outline" className="text-xs">
                        {issueCount} {issueCount === 1 ? 'issue' : 'issues'}
                    </Badge>
                ) : null}
            </div>
            <p className="min-w-0 text-sm [overflow-wrap:anywhere] break-all whitespace-pre-wrap">
                {rating.comment || 'No automated QA notes were saved.'}
            </p>
        </div>
    );
}

const DISPOSITION_LABELS: Record<string, string> = {
    engaged: 'Engaged',
    voicemail: 'Voicemail',
    unreachable: 'Unreachable',
    transferred: 'Transferred',
    wrong_contact: 'Wrong contact',
    error: 'Error',
};

function dispositionLabel(slug: string): string {
    return DISPOSITION_LABELS[slug] ?? formatBucketSlug(slug);
}

function AuditRunBody({ run }: { readonly run: AuditRun }) {
    const outcomeTopics = run.outcome_topics ?? [];
    const verdictBadge = run.verdict
        ? VOICE_QA_VERDICT_BADGES[run.verdict]
        : null;

    return (
        <div className="bg-muted/20 min-w-0 space-y-3 rounded-lg border p-3">
            <div className="flex flex-wrap items-center gap-2">
                {verdictBadge ? (
                    <Badge
                        variant="secondary"
                        className={cn('text-xs', verdictBadge.className)}
                    >
                        {verdictBadge.label}
                    </Badge>
                ) : null}
                {run.disposition ? (
                    <Badge variant="secondary" className="text-xs">
                        {dispositionLabel(run.disposition)}
                    </Badge>
                ) : null}
                {run.resolution_status ? (
                    <Badge
                        variant="secondary"
                        className={cn(
                            'text-xs',
                            run.resolution_status === 'resolved'
                                ? 'text-green-700'
                                : 'text-amber-700',
                        )}
                    >
                        {run.resolution_status === 'resolved'
                            ? 'Resolved'
                            : 'Not resolved'}
                    </Badge>
                ) : null}
                {outcomeTopics.slice(0, 2).map((topic, index) => (
                    <Badge
                        key={[
                            topic.bucket,
                            topic.secondary_bucket ??
                                topic.proposed_bucket ??
                                index,
                        ].join(':')}
                        variant="outline"
                        className="text-xs"
                    >
                        {formatBucketSlug(topic.bucket)}
                    </Badge>
                ))}
            </div>
            <p className="min-w-0 text-sm [overflow-wrap:anywhere] break-all whitespace-pre-wrap">
                {run.summary || 'No automated QA notes were saved.'}
            </p>
        </div>
    );
}

export function AutomatedVoiceQaSummary({
    callId,
    callStatus,
    className,
    isRerunning = false,
    onRerun,
}: {
    readonly callId: string;
    readonly callStatus?: string | null;
    readonly className?: string;
    readonly isRerunning?: boolean;
    readonly onRerun?: () => void;
}) {
    const { data: qaCall, isLoading } = useVoiceQACall(callId);
    const agentRating = qaCall?.ratings?.agent ?? null;
    // The admin voice-QA endpoint returns audit runs newest-first; this card
    // intentionally shows the latest post-call reconcile attempt.
    const latestPostCallRun =
        qaCall?.audit_runs?.find(
            (run) => run.phase === 'post_call_reconcile',
        ) ?? null;
    const latestSavedAt =
        latestPostCallRun?.created_at ?? agentRating?.created_at;

    return (
        <div className={cn('min-w-0 space-y-3', className)}>
            <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                    <h3 className="text-sm font-medium">Automated QA</h3>
                    {isLoading ? (
                        <div className="text-muted-foreground flex items-center gap-2 text-xs">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Checking automated QA status…
                        </div>
                    ) : latestSavedAt ? (
                        <p className="text-muted-foreground text-xs">
                            Saved{' '}
                            {formatDistanceToNow(new Date(latestSavedAt), {
                                addSuffix: true,
                            })}
                        </p>
                    ) : null}
                </div>
                <div className="flex shrink-0 flex-wrap justify-end gap-2">
                    {onRerun ? (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 gap-1.5 px-2 text-xs"
                            onClick={onRerun}
                            disabled={isRerunning}
                        >
                            {isRerunning ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <RefreshCcw className="h-3 w-3" />
                            )}
                            Rerun QA
                        </Button>
                    ) : null}
                    <a
                        className="inline-flex h-7 items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-300 dark:hover:text-blue-200"
                        href={`/admin/voice-qa/${callId}`}
                    >
                        Open QA debug page
                        <ExternalLink className="h-3 w-3" />
                    </a>
                </div>
            </div>
            {!isLoading && (
                <React.Fragment>
                    {latestPostCallRun ? (
                        <AuditRunBody run={latestPostCallRun} />
                    ) : agentRating ? (
                        <RatingBody rating={agentRating} />
                    ) : (
                        <div className="bg-muted/10 text-muted-foreground rounded-lg border border-dashed p-3 text-sm">
                            {getPendingCopy(qaCall, callStatus)}
                        </div>
                    )}
                </React.Fragment>
            )}
        </div>
    );
}
